import ArrowDown from '@src/svgs/ArrowDown';
import React, { useRef, useState } from 'react';
import styled from 'styled-components';

import DeleteIcon from '../../../svgs/DeleteIcon';
import DownloadIcon from '../../../svgs/DownloadIcon';
import PencilIcon from '../../../svgs/PencilIcon';
import ShareIcon from '../../../svgs/ShareIcon';
import { useOutsideAlerter } from '../../transcript/useClickOutside';
import QuoteDownloadDropdown from './QuoteDownloadDropdown';

import type { MediaFormat } from '../../common/FormatDropdown';

type Props = {
  onEdit: () => void;
  onDownload: (
    e: React.MouseEvent<HTMLButtonElement>,
    format?: MediaFormat,
  ) => void;
  onShare: () => void;
  onDelete: () => void;
  'data-testid'?: string;
};

const QuoteCardKebabMenu = ({
  onEdit,
  onDownload,
  onShare,
  onDelete,
  'data-testid': dataTestId,
}: Props) => {
  const [openDropdown, setOpenDropdown] = useState<boolean>(false);
  const [isDownloadDropdownOpen, setIsDownloadDropdownOpen] =
    useState<boolean>(false);
  const buttonRef = useRef<HTMLDivElement>(null);

  useOutsideAlerter(buttonRef, () => {
    setOpenDropdown(false);
    setIsDownloadDropdownOpen(false);
  });

  const handleDownloadClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    setIsDownloadDropdownOpen(true);
  };

  const handleDownloadSelect = (
    e: React.MouseEvent<HTMLButtonElement>,
    format: MediaFormat,
  ) => {
    onDownload(e, format);
    setIsDownloadDropdownOpen(false);
    setOpenDropdown(false);
  };

  const handleBackClick = () => {
    setIsDownloadDropdownOpen(false);
  };

  return (
    <Ellipsis
      onClick={e => {
        e.stopPropagation();
        setOpenDropdown(true);
      }}
      data-testid={dataTestId}
      data-kebab-menu
      $isOpen={openDropdown || isDownloadDropdownOpen}
    >
      <EllipsisIconWrapper>
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="var(--text-muted)"
        >
          <circle cx="12" cy="5" r="2" />
          <circle cx="12" cy="12" r="2" />
          <circle cx="12" cy="19" r="2" />
        </svg>
      </EllipsisIconWrapper>

      <MenuWrapper ref={buttonRef} isVisible={openDropdown}>
        {!isDownloadDropdownOpen ? (
          <MenuContainer>
            <MenuItems>
              <MenuItem onClick={onEdit}>
                <Content>
                  <IconAndText>
                    <IconWrapper>
                      <PencilIcon
                        width="14"
                        height="14"
                        strokeColor="var(--text-secondary)"
                      />
                    </IconWrapper>
                    <Label>Edit</Label>
                  </IconAndText>
                </Content>
              </MenuItem>

              <MenuItem onClick={handleDownloadClick}>
                <Content>
                  <IconAndText>
                    <IconWrapper>
                      <DownloadIcon
                        width="14"
                        height="14"
                        strokeColor="var(--text-secondary)"
                      />
                    </IconWrapper>
                    <Label>Download</Label>
                    <ChevronWrapper>
                      <ArrowDown
                        width="8"
                        height="5"
                        strokeColor="var(--text-secondary)"
                        strokeWidth="1.5"
                      />
                    </ChevronWrapper>
                  </IconAndText>
                </Content>
              </MenuItem>

              <MenuItem onClick={onShare}>
                <Content>
                  <IconAndText>
                    <IconWrapper>
                      <ShareIcon
                        width="14"
                        height="14"
                        strokeColor="var(--text-secondary)"
                        strokeWidth="1.5"
                      />
                    </IconWrapper>
                    <Label>Share</Label>
                  </IconAndText>
                </Content>
              </MenuItem>

              <MenuItem
                data-testid="quote-delete-option"
                onClick={onDelete}
                deleteButton
              >
                <Content>
                  <IconAndText>
                    <IconWrapper>
                      <DeleteIcon
                        width="14"
                        height="14"
                        strokeColor="currentColor"
                      />
                    </IconWrapper>
                    <Label>Delete</Label>
                  </IconAndText>
                </Content>
              </MenuItem>
            </MenuItems>
          </MenuContainer>
        ) : (
          <QuoteDownloadDropdown
            onDownload={handleDownloadSelect}
            onBack={handleBackClick}
          />
        )}
      </MenuWrapper>
    </Ellipsis>
  );
};

export default QuoteCardKebabMenu;

const Ellipsis = styled.div<{ $isOpen?: boolean }>`
  cursor: pointer;
  position: relative;
  z-index: 2;
  display: flex;
  align-items: center;
`;

const EllipsisIconWrapper = styled.div`
  display: flex;
  padding: 6px;
  border-radius: 4px;
  background: transparent;
  flex-direction: column;
  justify-content: center;
  transition: background var(--transition-fast);

  &:hover {
    background: var(--sidebar-hover);
  }
`;

const MenuWrapper = styled.div<{ isVisible: boolean }>`
  display: ${props => (props.isVisible ? 'flex' : 'none')};
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: 4px;
  z-index: 1000;
`;

const MenuContainer = styled.div`
  background: var(--background);
  box-shadow: var(--elevation-toast);
  border-radius: var(--radius);
  display: flex;
  flex-direction: column;
  align-items: stretch;
`;

const MenuItems = styled.div`
  border-radius: var(--radius);
  border: 1px solid var(--border);
  background: var(--background);
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  padding: var(--spacing-xs);
  overflow: hidden;
`;

const ChevronWrapper = styled.div`
  width: 12px;
  height: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  transform: rotate(-90deg);
  flex-shrink: 0;
`;

const MenuItem = styled.button<{ deleteButton?: boolean }>`
  display: flex;
  align-items: center;
  padding: 1px 6px;
  width: 123px;
  cursor: pointer;
  border: none;
  outline: none;
  background: none;
  border-radius: var(--radius-button);
  ${props =>
    props.deleteButton &&
    `
    color: var(--destructive);
    p { color: var(--destructive); }
  `}

  &:hover {
    background: ${props =>
      props.deleteButton ? 'var(--destructive)' : 'var(--surface-elevated)'};
    ${props =>
      props.deleteButton &&
      `
      color: var(--destructive-foreground);
      p, svg, span { color: var(--destructive-foreground); }
    `}
  }
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  flex: 1;
  padding: 9px 10px;
  border-radius: 6px;
  overflow: hidden;
  width: 100%;
`;

const IconAndText = styled.div`
  display: flex;
  align-items: center;
  text-align: start;
  gap: 8px;
  width: 100%;
  position: relative;
`;

const IconWrapper = styled.div`
  width: 14px;
  height: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const Label = styled.p`
  flex: 1;
  font-family: 'Inter', sans-serif;
  font-weight: 500;
  font-size: 12px;
  line-height: 20px;
  color: var(--text-secondary);
  margin: 0;
  white-space: pre-wrap;
`;
